import { useState, useRef, useCallback, useEffect } from 'react';
import { ChatMessage } from '../types/chat';

interface UseWebSocketChatConfig {
  autoConnect?: boolean;
  /**
   * Return a WebSocket URL when all prerequisites are met.
   * Return null/undefined to signal “not ready yet”.
   */
  buildUrl: () => string | null;
  /**
   * Who is sending from this browser.
   * role must be 'agent' | 'client'
   */
  currentUser: {
    id?: string | number;
    name: string;
    email?: string;
    picture?: string;
    role: 'agent' | 'client';
  };

  otherUser?: {
    name?: string;
    email?: string;
    picture?: string;
    role: 'client' | 'agent';
  };

  /**
   * Optional mock / seed messages (dev-only convenience).
   */
  seedMessages?: ChatMessage[];
  /**
   * Optional maximum reconnect attempts (default 5).
   */
  maxReconnectAttempts?: number;
}

export function useWebSocketChat({
  autoConnect,
  buildUrl,
  currentUser,
  otherUser,
  seedMessages = [],
  maxReconnectAttempts = 5
}: UseWebSocketChatConfig) {
  const [messages, setMessages]   = useState<ChatMessage[]>(seedMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(
    seedMessages.filter(m => !m.isRead && m.senderType !== currentUser.role).length
  );
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const wsRef                 = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef   = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef  = useRef(0);
  const keepAliveIntervalRef  = useRef<NodeJS.Timeout | null>(null);
  const manualCloseRef        = useRef(false);
  const isInitializedRef      = useRef(false);
  const typingTimeoutRef      = useRef<NodeJS.Timeout | null>(null);       // ← NEW

  // ────────────────────────────────────────────────────────────────
  const cleanupSocket = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };
  // ────────────────────────────────────────────────────────────────
  const disconnect = useCallback(() => {
    manualCloseRef.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    // Send disconnect message before closing if connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({
          messageType: "system",
          senderType: currentUser.role,
          systemType: "disconnect"
        }));
      } catch (err) {
        console.error('Failed to send disconnect message:', err);
      }
    }
    
    cleanupSocket();
    setIsConnected(false);
    setIsLoading(false);
    reconnectAttemptsRef.current = 0;
  }, [currentUser.role]);
  // ────────────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    manualCloseRef.current = false;

    // Guard against duplicate connect
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
       wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const url = buildUrl();
    if (!url) {
      setConnectionError('Chat not ready – missing requirements');
      return;
    }

    cleanupSocket();
    setIsLoading(true);
    setConnectionError(null);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsLoading(false);
        reconnectAttemptsRef.current = 0;

        keepAliveIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ messageType: 'ping', senderType: 'system' }));
          }
        }, 45_000);
      };

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);

          // History payload
          if (data.type === 'chat_history' && Array.isArray(data.messages)) {
            const history: ChatMessage[] = data.messages.map((m: any) => ({
              id: m.id || m.messageId || Date.now().toString(),
              senderId: m.senderId || m.sender_email || 'unknown',
              senderName: m.sender_name || m.senderName || getDisplayNameForSenderType(m.senderType),
              senderType: m.senderType,
              message: m.message || m.messageText,
              timestamp: m.timestamp || m.createdAt,
              isRead: m.isRead !== false
            }));
            setMessages(history);
            setUnreadCount(history.filter(msg => !msg.isRead && msg.senderType !== currentUser.role).length);
            return;
          }

          // Error payload
          if (data.error) {
            setConnectionError(data.error);
            return;
          }

          // Realtime message --------------------------------------------------
          if (data.messageType === 'text' && data.messageText) {
            const incoming: ChatMessage = {
              id: data.messageId || Date.now().toString(),
              senderId: data.sender_email || data.senderEmail || data.senderId,
              senderName: data.sender_name || data.senderName || getDisplayNameForSenderType(data.senderType),
              senderType: data.senderType,
              message: data.messageText,
              timestamp: data.timestamp || new Date().toISOString(),
              isRead: false
            };

            // ⬇️ NEW: remove any active typing placeholder for this sender
            setMessages(prev => [
              ...prev.filter(m => !(m.isTyping && m.senderType === data.senderType)),
              incoming
            ]);

            // stop pending timeout that would clear dots later
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = null;
            }

            if (incoming.senderType !== currentUser.role) {
              setUnreadCount(prev => prev + 1);
            }
            return;
          }

          // Realtime typing indicator
          if (data.messageType === 'typing') {
            // clear existing typing placeholders from same sender
            setMessages(prev => prev.filter(m => !(m.isTyping && m.senderType === data.senderType)));

            const placeholder: ChatMessage = {
              id        : `typing-${Date.now()}`,
              senderId  : data.senderId || data.sender_email || '',
              senderName: getDisplayNameForSenderType(data.senderType),
              senderType: data.senderType,
              message   : '',
              timestamp : new Date().toISOString(),
              isRead    : true,
              isTyping  : true
            };
            setMessages(prev => [...prev, placeholder]);

            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
              setMessages(prev => prev.filter(m => !m.isTyping));
            }, 3000);
            return;
          }
        } catch (err) {
          console.error('WS parse error', err);
        }
      };

      ws.onclose = (ev) => {
        setIsConnected(false);
        setIsLoading(false);
        cleanupSocket();

        if (manualCloseRef.current) {
          manualCloseRef.current = false;
          return;
        }

        if (ev.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30_000);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (err) => {
        console.error('WS error', err);
        setConnectionError('Connection error');
        setIsLoading(false);
      };
    } catch (err) {
      console.error('WS creation failed', err);
      setConnectionError('Failed to establish connection');
      setIsLoading(false);
    }
  }, [buildUrl, currentUser.role, otherUser, maxReconnectAttempts]);
  // Helper function to get display names based on sender type and available user info
  const getDisplayNameForSenderType = (senderType: 'agent' | 'client') => {
    if (senderType === currentUser.role) {
      return currentUser.name;
    } else if (otherUser?.name) {
      return otherUser.name;
    } else {
      return senderType === 'agent' ? 'Agent' : 'Client';
    }
  };
  // ────────────────────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (text: string): Promise<{ success: boolean; error?: string }> => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return { success: false, error: 'Not connected' };
      }
      if (!text.trim()) {
        return { success: false, error: 'Message cannot be empty' };
      }

      const payload = {
        messageText: text.trim(),
        messageType: 'text' as const,
        senderType: currentUser.role
      };

      try {
        wsRef.current.send(JSON.stringify(payload));

        const local: ChatMessage = {
          id: Date.now().toString(),
          senderId: currentUser.email || currentUser.id?.toString() || '',
          senderName: currentUser.name,
          senderType: currentUser.role,
          message: text.trim(),
          timestamp: new Date().toISOString(),
          isRead: true
        };
        setMessages(prev => [...prev, local]);
        return { success: true };
      } catch (err) {
        console.error('Send error', err);
        return { success: false, error: 'Failed to send' };
      }
    },
    [currentUser]
  );
  // ────────────────────────────────────────────────────────────────
  const sendTyping = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        messageType: 'typing',
        senderType : currentUser.role
      }));
    }
  }, [currentUser.role]);
  // ────────────────────────────────────────────────────────────────
  const markAsRead = useCallback(async () => {
    setMessages(prev => prev.map(m => ({ ...m, isRead: true })));
    setUnreadCount(0);
  }, []);
  // ────────────────────────────────────────────────────────────────
  const refreshMessages = useCallback(async () => {
    // no-op placeholder – server history is pushed on connect
    setMessages([]);
    setUnreadCount(0);
  }, []);
  // ────────────────────────────────────────────────────────────────
  // Auto-connect once when told
  useEffect(() => {
    if (autoConnect && !isInitializedRef.current) {
      isInitializedRef.current = true;
      connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect]);
  // ────────────────────────────────────────────────────────────────
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      manualCloseRef.current = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      cleanupSocket();
    };
  }, []);
  // ────────────────────────────────────────────────────────────────
  return {
    messages,
    isLoading,
    isConnected,
    unreadCount,
    connectionError,
    sendMessage,
    sendTyping,
    markAsRead,
    refreshMessages,
    connect,
    disconnect
  };
}